<template>
    <div class="cc-trend">
        <div class="cc-trend__plot">
            <div v-if="targetPercent !== null" class="cc-trend__target" :style="`bottom: ${toHeight(target)}%;`">
                <div class="cc-trend__target-label">Target {{ targetPercent }}%</div>
            </div>

            <div v-for="cycle in bars" :key="cycle.cycle_name" class="cc-trend__column">
                <div class="cc-trend__bar" :class="{ 'cc-trend__bar--current': cycle.is_current }" :style="`height: ${cycle.height}%;`">
                    <div class="cc-trend__value">{{ cycle.label }}</div>
                </div>
            </div>
        </div>

        <div class="cc-trend__axis">
            <div v-for="cycle in bars" :key="`axis-${cycle.cycle_name}`" class="cc-trend__axis-label">{{ cycle.shortName }}</div>
        </div>

        <div v-if="sources.length" class="cc-trend__sources">
            <div class="cc-trend__sources-title">
                Accept rate by objective source<span v-if="sourcesPeriod" class="cc-trend__sources-cycle"> · {{ sourcesPeriod }}</span>
            </div>
            <table class="cc-trend__sources-table">
                <thead>
                    <tr>
                        <th class="text-left">Objective Source</th>
                        <th v-for="source in orderedSources" :key="`source-${source.objective_source}`" class="text-right">{{ formatSource(source.objective_source) }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-left cc-trend__sources-label">Accept Rate</td>
                        <td v-for="source in orderedSources" :key="`rate-${source.objective_source}`" class="text-right">{{ formatPercent(source.accept_rate) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
const isMissing = (value) => value === null || value === undefined;

const SOURCE_ORDER = ["approved", "agent-recommended"];

export default {
    name: "CommandCenterAcceptRateTrend",

    props: {
        // [{ cycle_name, is_current, accept_rate }], oldest cycle first.
        cycles: {
            type: Array,
            required: true,
        },
        target: {
            type: Number,
            default: null,
        },
        // [{ objective_source, accept_rate }] for the current cycle, or for the custom date range.
        sources: {
            type: Array,
            default: () => [],
        },
        // The custom date range the sources cover, shown in place of the current cycle's name.
        periodLabel: {
            type: String,
            default: "",
        },
    },

    computed: {
        // The scale leaves headroom above the tallest bar or the target so their labels fit.
        scaleMax() {
            const rates = this.cycles.map((cycle) => Number(cycle.accept_rate) || 0);
            return Math.max(0.01, Number(this.target) || 0, ...rates) * 1.2;
        },

        targetPercent() {
            return isMissing(this.target) ? null : Math.round(this.target * 100);
        },

        // "Cycle 3 · 2026" is shown as "Cycle 3".
        bars() {
            return this.cycles.map((cycle) => ({
                ...cycle,
                shortName: String(cycle.cycle_name).split(" · ")[0],
                label: this.formatPercent(cycle.accept_rate),
                height: this.toHeight(cycle.accept_rate),
            }));
        },

        currentCycleName() {
            return this.bars.find((cycle) => cycle.is_current)?.shortName ?? null;
        },

        sourcesPeriod() {
            return this.periodLabel || this.currentCycleName;
        },

        // Approved is listed before Agent-Recommended; any other source follows.
        orderedSources() {
            const rank = (source) => {
                const index = SOURCE_ORDER.indexOf(String(source.objective_source ?? "").trim().toLowerCase());
                return index === -1 ? SOURCE_ORDER.length : index;
            };
            return [...this.sources].sort((a, b) => rank(a) - rank(b));
        },
    },

    methods: {
        toHeight(rate) {
            return Math.min(100, ((Number(rate) || 0) / this.scaleMax) * 100);
        },

        formatPercent(rate) {
            return isMissing(rate) ? "—" : `${Math.round(rate * 100)}%`;
        },

        // "agent-recommended" is shown as "Agent-Recommended".
        formatSource(source) {
            return String(source ?? "").toLowerCase().replace(/(^|[\s_-])(\w)/g, (_, separator, letter) => `${separator}${letter.toUpperCase()}`);
        },
    },
};
</script>

<style scoped>
.cc-trend {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.cc-trend__plot {
    position: relative;
    display: flex;
    align-items: flex-end;
    height: 120px;
}

.cc-trend__target {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    border-top: 1px dashed #9f9f9f;
}

.cc-trend__target-label {
    position: absolute;
    right: 0;
    bottom: 2px;
    color: #9f9f9f;
    font-size: 10px;
}

.cc-trend__column {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: flex-end;
    height: 100%;
}

.cc-trend__bar {
    position: relative;
    width: 60%;
    max-width: 72px;
    background: #a8a8a8;
    transition: height 0.3s ease-in-out;
}

.cc-trend__bar--current {
    background: #424242;
}

.cc-trend__value {
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-bottom: 3px;
    color: #262626;
    font-size: 11px;
    font-weight: 700;
    transform: translateX(-50%);
}

.cc-trend__bar--current .cc-trend__value {
    color: #424242;
}

.cc-trend__axis {
    display: flex;
    padding-top: 4px;
    border-top: 1px solid #dedede;
}

.cc-trend__axis-label {
    flex: 1;
    color: #5f5f5f;
    font-size: 10px;
    text-align: center;
}

.cc-trend__sources {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #dedede;
    font-size: 11px;
}

.cc-trend__sources-title {
    margin-bottom: 4px;
    color: #5f5f5f;
}

.cc-trend__sources-cycle {
    color: #5f5f5f;
    font-weight: 500;
}

.cc-trend__sources-table {
    border-collapse: collapse;
}

.cc-trend__sources-table th {
    padding: 4px 0;
    border-bottom: 1px solid #dedede;
    color: #5f5f5f;
    font-weight: 600;
}

.cc-trend__sources-table td {
    padding: 5px 0;
    color: #262626;
    font-weight: 700;
}

.cc-trend__sources-table th:not(:last-child),
.cc-trend__sources-table td:not(:last-child) {
    padding-right: 16px;
}

.cc-trend__sources-table .cc-trend__sources-label {
    color: #5f5f5f;
    font-weight: 600;
}
</style>
